import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import {
  ArrowLeft,
  Download,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { TemplateService } from "@/services/templateService";
import { Template } from "@/types/models/template";
import { usePages } from "@/hooks/usePages";

interface ImportStep {
  id: string;
  title: string;
  status: "pending" | "processing" | "completed" | "error";
  message?: string;
}

interface CustomPageData {
  title: string;
  slug: string;
  description: string;
  featured_image: string; // Asset ID
}

export default function ImportTemplatePage() {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const templateSlug = searchParams.get("templateSlug");
  const templateName = searchParams.get("templateName");
  const selectedPagesParam = searchParams.get("selectedPages");
  const customizationsParam = searchParams.get("customizations");

  // Use pages hook for real page creation
  const { form, actions } = usePages();

  const [template, setTemplate] = useState<Template | null>(null);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [customizations, setCustomizations] = useState<
    Record<string, CustomPageData>
  >({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMultiPageImport, setIsMultiPageImport] = useState(false);

  const [importSteps, setImportSteps] = useState<ImportStep[]>([
    { id: "validate", title: "Validating import data", status: "pending" },
    { id: "create", title: "Creating pages", status: "pending" },
    { id: "finalize", title: "Finalizing import", status: "pending" },
  ]);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      let templateData: Template;
      if (templateId) {
        templateData = await TemplateService.getTemplateById(templateId);
      } else if (templateSlug) {
        templateData = await TemplateService.getTemplateBySlug(templateSlug);
      } else {
        throw new Error("No template ID or slug provided");
      }
      setTemplate(templateData);
    } catch (err) {
      console.error("Failed to fetch template:", err);
      setError("Failed to load template details");
    } finally {
      setLoading(false);
    }
  }, [templateId, templateSlug]);

  useEffect(() => {
    if (!templateId && !templateSlug) {
      setError("No template ID provided");
      setLoading(false);
      return;
    }

    // Parse selected pages and customizations from URL
    let pageIds: string[] = [];
    let customData: Record<string, CustomPageData> = {};

    if (selectedPagesParam) {
      pageIds = selectedPagesParam.split(",");
      setSelectedPageIds(pageIds);
    }

    if (customizationsParam) {
      try {
        customData = JSON.parse(customizationsParam);
        setCustomizations(customData);
      } catch (err) {
        console.error("Failed to parse customizations:", err);
      }
    }

    // Determine import type
    const isSinglePage =
      pageIds.length === 1 && Object.keys(customData).length > 0;
    setIsMultiPageImport(!isSinglePage);

    fetchTemplate();
  }, [
    fetchTemplate,
    customizationsParam,
    selectedPagesParam,
    templateId,
    templateSlug,
  ]);

  const updateStepStatus = (
    stepId: string,
    status: ImportStep["status"],
    message?: string,
  ) => {
    setImportSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, status, message } : step,
      ),
    );
  };

  const startImport = async () => {
    if (!templateId || !template) {
      setError("Template data not available");
      return;
    }

    // Prevent multiple clicks while already importing or form is loading
    if (importing || form.loading) {

      return;
    }

    // Reset form state and error before starting
    actions.resetForm();
    setImporting(true);
    setError(null);

    try {
      // Step 1: Validate import data
      updateStepStatus("validate", "processing");
      await new Promise((resolve) => setTimeout(resolve, 800));
      updateStepStatus(
        "validate",
        "completed",
        "Import data validated successfully",
      );

      // Step 2: Create pages based on import type
      updateStepStatus("create", "processing");

      if (!isMultiPageImport) {
        // Single page import: Use regular pages API with template reference
        console.log("🚀 Starting single page import for template:", templateId);
        console.log("📋 Selected page:", selectedPageIds[0]);

        const pageId = selectedPageIds[0] || "";
        const customData: CustomPageData = pageId
          ? customizations[pageId] || {
              title: "",
              slug: "",
              description: "",
              featured_image: "",
            }
          : {
              title: "",
              slug: "",
              description: "",
              featured_image: "",
            };

        const pageData = {
          name: customData.title || `Page from ${templateName}`,
          slug: customData.slug || `page-${Date.now()}`,
          title: customData.title || `Page from ${templateName}`,
          description: customData.description || "",
          featured_image_asset_id: customData.featured_image || undefined,
          status: "draft" as const,
          template_id: templateId,
          template_page_id: pageId || undefined,
        };

        console.log("📄 Creating single page with data:", pageData);
        actions.createPageFromTemplate(pageData);
      } else {
        // Multiple pages import: Use from-template API endpoint
        console.log(
          "🚀 Starting multiple pages import for template:",
          templateId,
        );
        console.log("📋 Selected pages:", selectedPageIds);
        actions.createPagesFromTemplate(
          templateId,
          selectedPageIds.length > 0 ? selectedPageIds : undefined,
        );
      }

      // Note: Don't update step status to completed here - wait for the actual API response
    } catch (err) {
      console.error("Import failed:", err);
      setError(
        err instanceof Error ? err.message : "Import failed. Please try again.",
      );
      setImporting(false);
      // Reset all steps to pending
      setImportSteps((steps) =>
        steps.map((step) => ({ ...step, status: "pending" })),
      );
    }
  };

  // Listen for successful page creation
  useEffect(() => {
    // Only process form state changes when we're actually importing
    console.log("📊 Form state change:", {
      importing,
      formLoading: form.loading,
      formSuccess: form.success,
      formError: form.error,
    });

    if (!importing) return;

    if (form.success && !form.loading) {
      // Pages were created successfully, update create step and move to finalize

      updateStepStatus(
        "create",
        "completed",
        !isMultiPageImport
          ? `Successfully created single page from template`
          : `Successfully created ${selectedPageIds.length || "all"} page${(selectedPageIds.length || 2) === 1 ? "" : "s"} from template`,
      );

      // Move to finalize step
      updateStepStatus("finalize", "processing");
      setTimeout(() => {
        updateStepStatus(
          "finalize",
          "completed",
          "Import completed successfully",
        );
        setImportComplete(true);
        setImporting(false);
        // Note: Don't reset form here - let the user manually navigate away
      }, 500);
    }

    if (form.error && !form.loading) {
      // Handle error
      console.error("❌ Page creation failed:", form.error);
      const errorMessage =
        typeof form.error === "string"
          ? form.error
          : "Failed to create pages from template. Please try again.";
      setError(errorMessage);
      updateStepStatus("create", "error", errorMessage);
      setImporting(false);
      // Reset validation and create steps to pending (keep validate as completed)
      setImportSteps((steps) =>
        steps.map((step) =>
          step.id === "validate"
            ? step
            : { ...step, status: "pending", message: undefined },
        ),
      );
    }
  }, [
    form.success,
    form.loading,
    form.error,
    importing,
    isMultiPageImport,
    selectedPageIds.length,
  ]);

  // Cleanup form state when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Reset form state when component unmounts
      actions.resetForm();
    };
  }, [actions]);

  const handleBack = () => {
    if (importing) return; // Prevent navigation during import
    navigate(-1);
  };

  const handleGoToEditor = () => {
    if (!storeType || !storeId) return;
    // Navigate to webbuilder edit page - prefer onboarding-scoped edit when inside onboarding
    const inOnboarding = typeof window !== "undefined" && window.location.pathname.includes("/webbuilder/onboarding");
    const target = inOnboarding
      ? `/${storeType}/${storeId}/webbuilder/onboarding/edit`
      : `/${storeType}/${storeId}/webbuilder/edit`;
    navigate(target);
  };

  const getStepIcon = (status: ImportStep["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "processing":
        return (
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        );
      case "error":
        return <AlertCircle className="w-5 h-5 text-destructive" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-gradient-page dark:bg-gradient-page-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading template details...</p>
        </div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="h-screen w-full bg-gradient-page dark:bg-gradient-page-dark flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Error Loading Template 2
          </h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="space-x-4">
            <ButtonPrimary
              onClick={() => {
                if (!storeType || !storeId) return;
                navigate(`/${storeType}/${storeId}/webbuilder/onboarding`);
              }}
            >
              Go Back
            </ButtonPrimary>
            <ButtonOutline onClick={fetchTemplate}>Try Again</ButtonOutline>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-gradient-page dark:bg-gradient-page-dark overflow-auto">
      <div className="max-w-2xl mx-auto px-8 py-12">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8 animate-fadeInUp">
          <button
            onClick={handleBack}
            disabled={importing}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="w-px h-6 bg-border"></div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Import Template
            </h1>
            <p className="text-muted-foreground">
              {templateName
                ? `Importing "${templateName}"`
                : "Importing template"}
            </p>
          </div>
        </div>

        {/* Template Info */}
        {template && (
          <div
            className="bg-card dark:bg-auth-card rounded-xl border border-border dark:border-auth-background shadow-sm mb-8 animate-fadeInUp"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                  <Download className="w-8 h-8 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {template.name}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {template.description}
                  </p>
                  <div className="text-sm text-muted-foreground">
                    <span className="inline-flex items-center space-x-1">
                      <span>
                        {isMultiPageImport ? "Multi-page" : "Single page"}{" "}
                        import
                      </span>
                      {!isMultiPageImport && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 text-xs font-medium ml-2">
                          Customized
                        </span>
                      )}
                    </span>
                    <span className="ml-4">
                      {selectedPageIds.length} selected page
                      {selectedPageIds.length === 1 ? "" : "s"}
                    </span>
                    <span className="ml-4">
                      from {template.pages?.length || template.total_pages || 0}{" "}
                      total
                    </span>
                  </div>

                  {/* Show selected pages list */}
                  {selectedPageIds.length > 0 && template.pages && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-foreground mb-2">
                        {isMultiPageImport
                          ? "Pages to be imported:"
                          : "Custom page details:"}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {template.pages
                          .filter((page) => page && page.id && selectedPageIds.includes(page.id))
                          .map((page) => {
                            const customData = customizations[page.id];
                            const displayTitle =
                              customData?.title || page.title || page.name;
                            const displaySlug = customData?.slug || page.slug;

                            return (
                              <div
                                key={page.id}
                                className="flex items-center justify-between p-3 bg-muted dark:bg-auth-background rounded-lg"
                              >
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-foreground">
                                    {displayTitle}
                                  </p>
                                  {displaySlug && (
                                    <p className="text-xs text-muted-foreground">
                                      /{displaySlug}
                                    </p>
                                  )}
                                  {customData?.description &&
                                    !isMultiPageImport && (
                                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                        {customData.description}
                                      </p>
                                    )}
                                  {customData && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                      {isMultiPageImport
                                        ? "Customized"
                                        : "Custom metadata provided"}
                                    </p>
                                  )}
                                </div>
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              </div>
                            );
                          })}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {isMultiPageImport
                          ? "Selected pages will be imported and can be further customized in the editor."
                          : "Page will be created with your custom title, slug, description, and featured image."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Progress */}
        <div
          className="bg-card dark:bg-auth-card rounded-xl border border-border dark:border-auth-background shadow-sm animate-fadeInUp"
          style={{ animationDelay: "0.2s" }}
        >
          <div className="p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">
              Import Progress
            </h3>

            <div className="space-y-4 mb-6">
              {importSteps.map((step) => (
                <div key={step.id} className="flex items-center space-x-3">
                  {getStepIcon(step.status)}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-foreground">
                        {step.title}
                      </span>
                      {(step.status === "completed" ||
                        step.status === "error") &&
                        step.message && (
                          <span
                            className={`text-xs ${step.status === "error" ? "text-destructive" : "text-muted-foreground"}`}
                          >
                            - {step.message}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-4">
              {!importComplete && (
                <>
                  {!importing && !form.loading ? (
                    <ButtonPrimary onClick={startImport} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      Start Import
                    </ButtonPrimary>
                  ) : (
                    <div className="flex-1 flex items-center justify-center py-2 text-muted-foreground">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                      <span>
                        {form.loading
                          ? "Creating pages..."
                          : "Importing template..."}
                      </span>
                    </div>
                  )}
                  <ButtonOutline
                    onClick={handleBack}
                    disabled={importing || form.loading}
                  >
                    Cancel
                  </ButtonOutline>
                </>
              )}

              {importComplete && (
                <>
                  <ButtonPrimary onClick={handleGoToEditor} className="flex-1">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Go to Editor
                  </ButtonPrimary>
                  <ButtonOutline
                    onClick={() => {
                      if (!storeType || !storeId) return;
                      navigate(`/${storeType}/${storeId}/webbuilder/onboarding`);
                    }}
                  >
                    Import Another
                  </ButtonOutline>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
